<template>
  <div
    ref="heroSection"
    class="tw:bg-green-200 tw:pt-[40px] tw:md:pt-[60px] tw:min-h-[485px] tw:md:min-h-[600px] tw:relative"
  >
    <ContainerLayout class="tw:h-full tw:flex tw:flex-col tw:md:flex-row tw:gap-y-10">
      <div
        class="tw:w-full tw:md:w-3/5 tw:lg:w-1/2 tw:flex tw:flex-col tw:gap-[40px] tw:md:gap-y-12 tw:lg:gap-y-[70px] tw:md:mr-16 tw:lg:mr-auto"
      >
        <div class="tw:flex tw:flex-col tw:gap-y-4">
          <h1
            class="tw:text-[32px] tw:lg:text-[44px] tw:leading-[40px] tw:lg:leading-[55px]"
            v-html="$t('rc_product_page_header_new')"
          />
          <h1
            class="tw:text-[32px] tw:lg:text-[44px] tw:leading-[40px] tw:lg:leading-[55px]"
            v-html="$t('rc_product_page_subtext')"
          />
        </div>
        <div>
          <p v-if="!hasAdminOrSupport" class="tw:mb-6" v-html="$t('rc_product_page_admin')" />

          <p v-else class="tw:mb-6" v-html="$t('rc_product_page_subline')" />
          <PartnerSelector
            :withShadow="true"
            :partners="activePartnersData"
            :selectedPartner="selectedPartner"
            :isAdmin="hasAdminOrSupport"
            @selectPartner="selectPartner"
            @startCreatingCampaign="startCreatingCampaign"
          />
        </div>
      </div>

      <div class="tw:w-full tw:md:w-2/5 tw:lg:w-1/2 tw:flex tw:items-end tw:justify-end tw:h-full">
        <img
          :src="heroImage"
          alt="Rate Connect"
          class="tw:mx-auto tw:max-h-[388px] tw:md:max-h-[431px] tw:lg:max-h-[503px] tw:w-auto tw:md:absolute tw:bottom-0"
        />
      </div>
    </ContainerLayout>
  </div>
</template>
<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

import ContainerLayout from '@/layouts/ContainerLayout.vue'
import PartnerSelector from '@/components/rateConnect/onboarding/PartnerSelector.vue'

import { usePartners } from '@/components/rateConnect/queries'
import { selectedAccommodationId } from '@/layouts/queries'
import { useIsMobile } from '@/composables/useIsMobile'
import { useRateConnectOnboardingData } from '@/components/rateConnect/onboarding/useRateConnectOnboardingData'
import { useSetup } from '../setup/useSetup'
import { useSessionValues } from '@/composables/useSessionValues'

import appAnalytics from '@/utils/tracking'

const props = defineProps({
  isEligible: Boolean,
})
const heroImages = import.meta.glob('/src/assets/img/rate-connect/hero-image/*.png', {
  eager: true,
  query: '?url',
  import: 'default',
})

const store = useStore()
const router = useRouter()
const { initScrollObserver, cleanup, resetShowAlternateHeader } = useRateConnectOnboardingData()
const { selectPartner, selectedPartner } = useSetup()
const { screenWidth } = useIsMobile()
const screenSize = computed(() => {
  if (screenWidth.value > 1024) {
    return 'l'
  } else if (screenWidth.value > 768) {
    return 'm'
  } else if (screenWidth.value >= 500) {
    return 's'
  } else {
    return 'xs'
  }
})
const heroSection = ref(null)
const { hasAdminOrSupport, language } = useSessionValues()
const currentLocation = computed(() => language.value.split('-')[1].toLowerCase())
const { activePartnersData } = usePartners(selectedAccommodationId)

const heroImage = computed(() => {
  const path = `/src/assets/img/rate-connect/hero-image/hero_image_${screenSize.value}_${currentLocation.value}.png`
  return heroImages[path] || ''
})

onMounted(() => {
  initScrollObserver(heroSection.value, props.isEligible)
})

onUnmounted(() => {
  cleanup()
})

watch(
  activePartnersData,
  newValue => {
    if (newValue.length > 0) {
      selectPartner(newValue[0])
    }
  },
  { immediate: true }
)

const startCreatingCampaign = () => {
  resetShowAlternateHeader()
  if (hasAdminOrSupport.value) {
    router.push({ name: 'rate-connect-setup' })
    appAnalytics.track(appAnalytics.events.RC_CREATE_CAMPAIGN_CLICKED, {
      source: 'hero-section',
      accommodationId: selectedAccommodationId.value,
    })
  } else {
    store.dispatch('session/logout', '/rate-connect/no-campaign?loginAsAdmin=true')
    appAnalytics.track(appAnalytics.events.RC_LOGIN_AS_ADMIN_CLICKED, {
      accommodationId: selectedAccommodationId.value,
    })
  }
}
</script>
